import { useNavigate } from 'react-router-dom';
import type { WalletLedgerRow } from '@capsico/contracts';
import { useI18n } from '../i18n';
import { refundRowView } from '../i18n/labels';
import { useWallet } from '../hooks/useWallet';
import { money } from '../utils/money';
import { dayMonth, orderTime } from '../utils/format';
import { VirtualList } from '../components/VirtualList';
import { CreditRow, LEDGER_ROW_HEIGHT, LedgerRow, WalletHero } from '../components/Wallet';
import { RefundItem } from '../components/Payment';
import { CashbackLine, WinbackCard } from '../components/Growth';
import {
  Button,
  Card,
  EmptyState,
  ListSkeleton,
  Notice,
  PageHeader,
  Pill,
  SectionTitle,
} from '../components/ui';

/** Credits that expire within this many days get the amber "expires soon" line. */
const SOON_MS = 7 * 86_400_000;

/**
 * Capsico Money (W11, inherited AA): balance hero, refunds waiting for a choice, live credits
 * with their expiry, and the full history (virtualised past 100 rows). Store credit only — there
 * is no top-up anywhere. Data and the refund choice live in useWallet.
 */
export function WalletPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const w = useWallet({ saved: t('refund.saved') });
  const wallet = w.wallet;
  const country = w.country;
  const now = Date.now();

  const entryTitle = (row: WalletLedgerRow) => row.note ?? t(`wallet.entry.${row.entry}`);
  const signed = (minor: number) =>
    minor < 0 ? `− ${money(country, -minor)}` : `+ ${money(country, minor)}`;

  return (
    <div className="min-h-dvh bg-page pb-10">
      <PageHeader
        title={t('wallet.title')}
        onBack={() => navigate(-1)}
        backLabel={t('common.back')}
      />
      <div className="flex flex-col gap-5 px-4 pt-4">
        {w.countries.length > 0 && (
          <div role="radiogroup" aria-label={t('wallet.title')} className="flex gap-2">
            {w.countries.map((code) => (
              <Pill key={code} active={country === code} onClick={() => w.setCountry(code)}>
                {t(`wallet.country.${code}`)}
              </Pill>
            ))}
          </div>
        )}

        <WalletHero
          label={t('wallet.balance')}
          balance={money(country, wallet?.balanceMinor ?? 0)}
          hint={t('wallet.heroHint')}
          loading={w.loading}
        />
        {w.winback && (
          <WinbackCard
            title={t('winback.title')}
            body={t('winback.body', {
              amount: money(country, w.winback.remainingMinor),
              date: dayMonth(w.winback.expiresAt),
            })}
          />
        )}
        <Notice tone="info" title={t('wallet.storeCredit')} />

        {w.failed ? (
          <Notice
            tone="negative"
            supportRef
            title={t('common.wrong')}
            action={
              <Button size="sm" tone="outline" onClick={w.retry}>
                {t('common.retry')}
              </Button>
            }
          >
            {w.error}
          </Notice>
        ) : w.loading || !wallet ? (
          <Card>
            <ListSkeleton rows={4} />
          </Card>
        ) : (
          <>
            {wallet.pendingChoices.length > 0 && (
              <section>
                <SectionTitle>{t('wallet.pending')}</SectionTitle>
                <Card>
                  <div className="divide-y divide-hair">
                    {wallet.pendingChoices.map((refund) => (
                      <RefundItem
                        key={refund.id}
                        row={refundRowView(t, country, refund, refund.orderNumber)}
                        busy={w.choosingId === refund.id}
                        onChoose={(destination) => w.choose(refund.id, destination)}
                      />
                    ))}
                  </div>
                </Card>
              </section>
            )}

            <section>
              <SectionTitle>{t('wallet.credits')}</SectionTitle>
              {wallet.credits.length === 0 ? (
                <Card>
                  <EmptyState icon="wallet" title={t('wallet.empty')}>
                    {t('wallet.emptyHint')}
                  </EmptyState>
                </Card>
              ) : (
                <Card flush>
                  <div className="divide-y divide-hair">
                    {wallet.credits.map((credit) => (
                      <CreditRow
                        key={credit.id}
                        title={t(`wallet.source.${credit.source}`)}
                        note={credit.note}
                        left={
                          credit.remainingMinor === credit.amountMinor
                            ? money(country, credit.remainingMinor)
                            : t('wallet.left', {
                                left: money(country, credit.remainingMinor),
                                total: money(country, credit.amountMinor),
                              })
                        }
                        expiry={t('wallet.expiresOn', { date: dayMonth(credit.expiresAt) })}
                        soon={new Date(credit.expiresAt).getTime() - now < SOON_MS}
                      />
                    ))}
                  </div>
                </Card>
              )}
            </section>

            {w.cashbackRows.length > 0 && (
              <section>
                <SectionTitle>{t('cashback.walletTitle')}</SectionTitle>
                {w.cashbackPercent !== null && (
                  <p className="-mt-1 mb-2.5 text-xs text-ink-2">
                    {t('cashback.walletTotal', {
                      amount: money(country, w.cashbackTotalMinor),
                      percent: w.cashbackPercent,
                    })}
                  </p>
                )}
                <Card flush>
                  <div className="divide-y divide-hair">
                    {w.cashbackRows.slice(0, 20).map((row) => (
                      <CashbackLine
                        key={row.orderId}
                        title={
                          row.orderNo !== null
                            ? t('cashback.order', { number: row.orderNo })
                            : t('cashback.orderNoNumber')
                        }
                        sub={
                          row.expiresAt
                            ? t('cashback.expires', { date: dayMonth(row.expiresAt) })
                            : orderTime(row.createdAt)
                        }
                        amount={`+ ${money(country, row.amountMinor)}`}
                      />
                    ))}
                  </div>
                </Card>
              </section>
            )}

            <section>
              <SectionTitle>{t('wallet.history')}</SectionTitle>
              {wallet.ledger.length === 0 ? (
                <Card>
                  <p className="text-center text-xs text-ink-3">{t('wallet.noHistory')}</p>
                </Card>
              ) : (
                <Card flush>
                  <VirtualList
                    items={wallet.ledger}
                    rowHeight={LEDGER_ROW_HEIGHT}
                    getKey={(row) => row.id}
                    renderRow={(row) => (
                      <LedgerRow
                        title={entryTitle(row)}
                        sub={
                          row.orderNumber ? t('orders.number', { number: row.orderNumber }) : null
                        }
                        when={orderTime(row.at)}
                        amount={signed(row.amountMinor)}
                        credit={row.amountMinor > 0}
                      />
                    )}
                  />
                </Card>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
